// src/api/finance.js
import api from "./api"; // centralized axios instance with baseURL + auth token

/**
 * 🔍 Fetch all pending finance approvals
 */
export const getPendingFinanceRequests = async () => {
  try {
    const res = await api.get("/finance-approvals/pending/");
    return res.data;
  } catch (error) {
    console.error("Error fetching pending finance approvals:", error);
    throw error;
  }
};

/**
 * 📜 Fetch all finance approvals history (approved/rejected)
 */
export const getFinanceHistory = async () => {
  try {
    const res = await api.get("/finance-approvals/");
    return res.data;
  } catch (error) {
    console.error("Error fetching finance approvals history:", error);
    throw error;
  }
};

/**
 * ✅ Mark a single request as ordered
 * @param {number} id - Finance approval ID
 * @param {object} updates - Optional payload (e.g. PO number, order date)
 */
export const markAsOrdered = async (id, updates = {}) => {
  try {
    const res = await api.patch(`/finance-approvals/${id}/`, {
      status: "ORDERED",
      ...updates,
    });
    return res.data;
  } catch (error) {
    console.error(`Error marking request ${id} as ordered:`, error);
    throw error;
  }
};

/**
 * ❌ Reject a single finance request
 * @param {number} id - Finance approval ID
 * @param {string} comments - Rejection reason
 */
export const rejectFinanceRequest = async (id, comments = "") => {
  try {
    const res = await api.patch(`/finance-approvals/${id}/`, {
      status: "REJECTED",
      comments,
    });
    return res.data;
  } catch (error) {
    console.error(`Error rejecting finance request ${id}:`, error);
    throw error;
  }
};

/**
 * 🟩 Bulk Approve multiple finance requests
 * @param {number[]} ids - Array of finance approval IDs
 * @param {string} comments - Optional approval note
 */
export const bulkApproveFinanceRequests = async (ids, comments = "") => {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error("bulkApproveFinanceRequests requires an array of IDs");
  }

  try {
    const res = await api.patch("/finance-approvals/bulk/", {
      request_ids: ids,
      status: "ORDERED",
      comments,
    });
    return res.data;
  } catch (error) {
    console.error("Error in bulkApproveFinanceRequests:", error);
    throw error;
  }
};

/**
 * 🟥 Bulk Reject multiple finance requests
 * @param {number[]} ids - Array of finance approval IDs
 * @param {string} comments - Rejection reason
 */
export const bulkRejectFinanceRequests = async (ids, comments = "") => {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error("bulkRejectFinanceRequests requires an array of IDs");
  }

  try {
    const res = await api.patch("/finance-approvals/bulk/", {
      request_ids: ids,
      status: "REJECTED",
      comments,
    });
    return res.data;
  } catch (error) {
    console.error("Error in bulkRejectFinanceRequests:", error);
    throw error;
  }
};

/**
 * 🔢 Bulk Action by Lot Number (approve or reject entire lot)
 * - Applies to all pending finance approvals in a specific lot
 * - Can also target only specific item IDs
 *
 * @param {string} lotNumber - Lot number (e.g. "L01-OfficeSupplies")
 * @param {"ORDERED" | "PAID" | "REJECTED"} status - Target action
 * @param {string} comments - Optional remarks
 * @param {number[]} ids - Optional specific IDs to target
 */
export const bulkFinanceActionByLot = async (lotNumber, status, comments = "", ids = []) => {
  if (!lotNumber && (!Array.isArray(ids) || ids.length === 0)) {
    throw new Error("bulkFinanceActionByLot requires a lot number or an array of IDs");
  }

  try {
    const payload = { status, comments };
    if (ids.length > 0) {
      payload.request_ids = ids;
    } else {
      payload.lot_number = lotNumber;
    }

    const res = await api.patch("/finance-approvals/bulk/", payload);
    return res.data;
  } catch (error) {
    console.error("Error in bulkFinanceActionByLot:", error);
    throw error;
  }
};
